import { useCallback, useRef } from "react";

interface Props {
  /** "x" drags horizontally (pane width), "y" vertically (pane height). */
  axis: "x" | "y";
  /** Which way a positive drag grows the pane. */
  invert?: boolean;
  onResize: (deltaPx: number) => void;
  label: string;
}

/** A drag strip between two panes. Panes resize; they do not rearrange. */
export function ResizeHandle({ axis, invert = false, onResize, label }: Props) {
  const last = useRef(0);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      last.current = axis === "x" ? event.clientX : event.clientY;

      function move(e: PointerEvent) {
        const now = axis === "x" ? e.clientX : e.clientY;
        const delta = now - last.current;
        last.current = now;
        onResize(invert ? -delta : delta);
      }
      function up(e: PointerEvent) {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", up);
      }
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", up);
    },
    [axis, invert, onResize],
  );

  return (
    <div
      className={`resize-handle resize-handle-${axis}`}
      role="separator"
      aria-label={label}
      aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      onPointerDown={onPointerDown}
    />
  );
}
